/**
 * Парсит QR-код в различных форматах:
 * 1. GS1 формат: 0106921054602658112503211730032010D25032101
 *    - 01: GTIN (14 цифр)
 *    - 11: Дата производства (YYMMDD)
 *    - 17: Дата истечения срока годности (YYMMDD)
 *    - 10: Номер партии
 * 2. Текстовый формат: PC: 06921054602658 LOT: D25032101 PROD: 21.03.2025 EXP: 20.03.2030
 * 
 * @param {string} qrText - Текст QR-кода
 * @returns {Object} Объект с распарсенными данными
 */
export const parseQrCode = (qrText) => {
  if (!qrText || typeof qrText !== 'string') {
    console.warn('⚠️ parseQrCode: Invalid input', qrText);
    return null;
  }

  // Удаляем ведущие символы GS1 (Group Separator \x1D или \u001d)
  // и другие невидимые символы, затем обрезаем пробелы
  let cleanedText = qrText.replace(/^[\x1D\u001D\u001d\s]+/, '').trim();
  
  // Если после очистки строка пустая, используем оригинальную
  if (!cleanedText) {
    cleanedText = qrText.trim();
  }
  
  console.log('🔍 Parsing QR code:', cleanedText);
  console.log('🔍 Original QR code:', qrText);
  console.log('🔍 Cleaned QR code:', cleanedText);

  const result = {
    product_code: null,
    lot_number: null,
    production_date: null,
    expiry_date: null,
    qr_code: cleanedText, // Сохраняем очищенный QR-код
  };

  // Проверяем формат GS1 (начинается с Application Identifiers "01" и 14 цифр GTIN)
  // Пример: 0106921054602658112503211730032010D25032101
  const isGS1Format = /^01\d{14}/.test(cleanedText);
  
  if (isGS1Format) {
    console.log('📦 Detected GS1 format');
    
    let currentPos = 0;
    
    // Парсим GTIN (01) - 14 цифр после "01"
    const gtinMatch = cleanedText.substring(currentPos).match(/^01(\d{14})/);
    if (gtinMatch) {
      result.product_code = gtinMatch[1];
      currentPos += gtinMatch[0].length; // Продвигаем позицию: "01" + 14 цифр
      console.log('✅ Found GTIN (01):', result.product_code, 'at position', currentPos);
    }
    
    // Парсим дату производства (11) - 6 цифр YYMMDD
    const prodMatch = cleanedText.substring(currentPos).match(/^11(\d{6})/);
    if (prodMatch) {
      const dateStr = prodMatch[1];
      // Формат YYMMDD -> YYYY-MM-DD
      const year = '20' + dateStr.substring(0, 2);
      const month = dateStr.substring(2, 4);
      const day = dateStr.substring(4, 6);
      result.production_date = `${year}-${month}-${day}`;
      currentPos += prodMatch[0].length; // Продвигаем позицию: "11" + 6 цифр
      console.log('✅ Found Production Date (11):', dateStr, '->', result.production_date, 'at position', currentPos);
    }
    
    // Парсим дату истечения срока годности (17) - 6 цифр YYMMDD
    const expMatch = cleanedText.substring(currentPos).match(/^17(\d{6})/);
    if (expMatch) {
      const dateStr = expMatch[1];
      // Формат YYMMDD -> YYYY-MM-DD
      const year = '20' + dateStr.substring(0, 2);
      const month = dateStr.substring(2, 4);
      const day = dateStr.substring(4, 6);
      result.expiry_date = `${year}-${month}-${day}`;
      currentPos += expMatch[0].length; // Продвигаем позицию: "17" + 6 цифр
      console.log('✅ Found Expiry Date (17):', dateStr, '->', result.expiry_date, 'at position', currentPos);
    }
    
    // Парсим номер партии (10) - идет после "10", до следующего AI или конца строки
    // В GS1 формат 10 может быть переменной длины (до 20 символов)
    // Ищем "10" который идет после всех предыдущих данных
    const lotMatch = cleanedText.substring(currentPos).match(/^10(.+)$/);
    if (lotMatch) {
      result.lot_number = lotMatch[1];
      console.log('✅ Found Lot Number (10):', result.lot_number);
    } else {
      console.warn('⚠️ Lot Number (10) not found after position', currentPos);
      console.warn('   Remaining text:', cleanedText.substring(currentPos));
    }
    
  } else {
    // Парсим текстовый формат: PC: ... LOT: ... PROD: ... EXP: ...
    console.log('📝 Detected text format');
    
    // Парсим PC (Product Code)
    const pcMatch = cleanedText.match(/PC:\s*(\S+)/i);
    if (pcMatch) {
      result.product_code = pcMatch[1].trim();
      console.log('✅ Found PC:', result.product_code);
    }

    // Парсим LOT (Lot Number)
    const lotMatch = cleanedText.match(/LOT:\s*(\S+)/i);
    if (lotMatch) {
      result.lot_number = lotMatch[1].trim();
      console.log('✅ Found LOT:', result.lot_number);
    }

    // Парсим PROD (Production Date) - формат DD.MM.YYYY
    const prodMatch = cleanedText.match(/PROD:\s*(\d{2}\.\d{2}\.\d{4})/i);
    if (prodMatch) {
      const dateStr = prodMatch[1];
      const [day, month, year] = dateStr.split('.');
      result.production_date = `${year}-${month}-${day}`;
      console.log('✅ Found PROD:', dateStr, '->', result.production_date);
    }

    // Парсим EXP (Expiry Date) - формат DD.MM.YYYY
    const expMatch = cleanedText.match(/EXP:\s*(\d{2}\.\d{2}\.\d{4})/i);
    if (expMatch) {
      const dateStr = expMatch[1];
      const [day, month, year] = dateStr.split('.');
      result.expiry_date = `${year}-${month}-${day}`;
      console.log('✅ Found EXP:', dateStr, '->', result.expiry_date);
    }
  }

  // Всегда возвращаем результат, даже если некоторые поля не найдены
  const hasParsedData = result.product_code || result.lot_number || result.production_date || result.expiry_date;
  
  if (hasParsedData) {
    console.log('✅ QR code parsed successfully:', result);
  } else {
    console.warn('⚠️ No recognizable data found in QR code format');
  }
  
  return result;
};

/**
 * Проверяет, является ли текст QR-кодом нужного формата
 * @param {string} qrText - Текст для проверки
 * @returns {boolean}
 */
export const isQrCodeFormat = (qrText) => {
  if (!qrText || typeof qrText !== 'string') {
    return false;
  }
  
  // Удаляем ведущие символы GS1 (Group Separator \x1D или \u001d)
  const cleanedText = qrText.replace(/^[\x1D\u001D\u001d\s]+/, '').trim() || qrText.trim();
  
  // Проверяем оба формата: GS1 и текстовый
  return /^01\d{14}/.test(cleanedText) || /PC:\s*\S+/i.test(cleanedText);
};

